import Helpers from './Helpers.js';

/**
 * Chat — everything Chili Piper Chat: widget bootstrap, state sync, and
 * widget events. The bar's Chat button is the ONLY launcher; CP's own
 * launcher bubble/teaser never shows.
 *
 * How it hangs together:
 *   - schedule() loads the CP widget on first interaction or idle. The
 *     Chat button is x-show-gated on chatState === 'ready', so it only
 *     appears once the widget iframe exists.
 *   - Opening is native CP: a Custom Trigger on #thryv-connect-bar-chat
 *     (CP admin → Web Experience → Settings → Custom Triggers) runs the
 *     published journey from the button's own click.
 *   - Visibility is synced FROM the widget's state by watchFrame(): the CP
 *     loader mirrors widget state onto the iframe's class ("expanded" =
 *     open conversation), flipping BEFORE its resize animation — the CSS
 *     in sass/index.scss hides any non-expanded frame outright.
 *   - `suppressed` starts true (CP persists an open conversation across
 *     pages and would auto-pop otherwise) and gates the sync: hidden until
 *     an explicit Chat click, re-hidden by toggle-close / other panels.
 */

var Chat = new Object();

/**
 * Visibility suppression — true means the widget stays hidden regardless
 * of CP's own (possibly open) state. Lifted only by a Chat-button open.
 */
Chat.suppressed = true;

/**
 * The MutationObserver watching the CP iframe's class (set by watchFrame).
 * Doubles as its run-once guard — truthy means already watching.
 */
Chat.frameObserver = null;

/**
 * Load the CP widget when the visitor shows up, not at page load (perf, 2026-10).
 *
 * The widget is ~1.45 MB of Chili Piper JS + an iframe — the heaviest thing on the page, fetched
 * and parsed in the middle of first render for a button most visitors never press. It now loads
 * on whichever comes first:
 *   - the first sign of a person: pointerdown, keydown, touchstart or scroll;
 *   - SCHEDULE_TIMEOUT ms after the window load event, during idle time — so a visitor who just
 *     reads still has the Chat button within a few seconds of the page settling.
 * The Chat button is x-show-gated on chatState === 'ready', so it cannot be clicked before the
 * widget exists — there is no dead click to queue. A conversation CP restores from a previous
 * page stays suppressed exactly as before.
 *
 * @param {object} component  Alpine component
 */
Chat.SCHEDULE_TIMEOUT = 5000;

Chat.schedule = function (component) {
  var _this = this;

  if (!component.chatEnabled || !component.chat || !component.chat.loaderSrc) return;

  var events = ["pointerdown", "keydown", "touchstart", "scroll"];
  var timer = null;
  var started = false;

  var start = function () {
    if (started) return;
    started = true;

    events.forEach(function (name) {
      window.removeEventListener(name, start, true);
    });
    if (timer) clearTimeout(timer);

    _this.init(component);
  };

  events.forEach(function (name) {
    window.addEventListener(name, start, { capture: true, passive: true });
  });

  var startWhenIdle = function () {
    timer = setTimeout(function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(start, { timeout: 2000 });
      } else {
        start();
      }
    }, _this.SCHEDULE_TIMEOUT);
  };

  if (document.readyState === "complete") {
    startWhenIdle();
  } else {
    window.addEventListener("load", startWhenIdle, { once: true });
  }
};

/**
 * Load the CP chat widget (called by schedule() above, on first interaction or
 * idle — see there). No iframe within the polling window =
 * journey unpublished or loader broken → chatState 'unavailable' and the
 * Chat button just never appears.
 *
 * @param {object} component  Alpine component
 */
Chat.init = function (component) {

  var _this = this;

  if (!component.chatEnabled || !component.chat || !component.chat.loaderSrc) return;

  if (component.chatState !== 'idle') return;

  _this.suppressed = true;

  component.chatState = 'loading';

  window.ChiliChat = window.ChiliChat || {
    config: {
      workspaceId: component.chat.workspaceId,
      tenantId: component.chat.tenantId,
      chatUrl: component.chat.chatUrl,
      eventsHandler: function (cpEvent) {

        _this.widgetEvent(component, cpEvent);

      },
    },
  };

  Helpers.loadScripts([{ src: component.chat.loaderSrc, module: true, crossorigin: 'anonymous' }])
    .then(function () {

      // The widget mounts its iframe asynchronously (loader → journey
      // fetch → mount) — poll for the element (not its size: it mounts
      // hidden).
      var attempts = 0;
      var waitForFrame = setInterval(function () {

        if (document.getElementById('chili-chat')) {

          clearInterval(waitForFrame);

          component.chatState = 'ready';

          _this.watchFrame(component);

          Helpers.log(component, 'Chat.init — widget mounted, Chat button revealed');

          return;

        }

        if (++attempts >= 50) { // ~15s

          clearInterval(waitForFrame);

          component.chatState = 'unavailable';

          Helpers.log(component, 'Chat.init — widget never mounted (journey unpublished?), Chat button stays hidden');

        }

      }, 300);

    })
    .catch(function (error) {

      component.chatState = 'unavailable';

      Helpers.log(component, 'Chat.init — loader failed', error);

    });

};

/**
 * CP chat widget events — the loader calls ChiliChat.config.eventsHandler
 * with EVERY widget event ({ action, args }). Placeholder hook points, log
 * only until tracking is speced. Known actions (from the loader's dispatch
 * table — the same ones it maps to its optional onTriggered/onSuccess/
 * onError config callbacks):
 *
 *   chat-targeted      the journey targeted/triggered for this visitor
 *   booking-confirmed  a meeting was booked INSIDE the chat conversation
 *   playbook-error     journey broken
 *   no-playbook        no published journey for this visitor/page
 *   booking-error      in-chat booking failed
 *
 * @param {object} component  Alpine component
 * @param {object} cpEvent    { action, args } from the CP loader
 */
Chat.widgetEvent = function (component, cpEvent) {

  Helpers.log(component, 'chat widget event', cpEvent);

  switch (cpEvent && cpEvent.action) {

    // Journey triggered for this visitor.
    case 'chat-targeted':
      break;

    // The chat-attribution conversion moment — a demo booked inside chat.
    // When tracking is speced, dispatch here so GTM can pick it up, e.g.:
    // Helpers.dispatch('connect_bar_chat_booking', { event: 'connect_bar_chat_booking', page_url: window.location.href });
    case 'booking-confirmed':
      break;

    // Journey unpublished/broken, or an in-chat booking failed.
    case 'playbook-error':
    case 'no-playbook':
    case 'booking-error':
      break;

  }

};

/**
 * Sync visibility from the widget's real state. The CP loader mirrors the
 * widget state onto the iframe's class (its WIDGET_STATE_CHANGE handler
 * does iframe.className = state; "expanded" = open conversation, anything
 * else = launcher bubble/teaser), and the class flips BEFORE CP's resize
 * animation. Expanded → visible unless suppressed; leaving expanded while
 * we're open = the visitor closed chat with CP's own X. The CSS also hides
 * any non-expanded frame outright, so the bubble can't even flash between
 * class flip and this callback.
 *
 * @param {object} component  Alpine component
 */
Chat.watchFrame = function (component) {

  var _this = this;

  if (_this.frameObserver || typeof MutationObserver !== 'function') return;

  var frame = document.getElementById('chili-chat');

  if (!frame) return;

  var sync = function () {

    var expanded = frame.classList.contains('expanded');

    if (expanded && !_this.suppressed) {

      document.body.classList.add('cb-chat-open');

      component.chatOpen = true;

      return;

    }

    if (!expanded && component.chatOpen) {

      Helpers.log(component, 'Chat.watchFrame — CP-internal close');

      _this.hide(component);

    }

  };

  _this.frameObserver = new MutationObserver(sync);

  _this.frameObserver.observe(frame, { attributes: true, attributeFilter: ['class'] });

  sync();

};

/**
 * Reveal the widget: lift the suppression and, if the (persisted)
 * conversation is already expanded behind it, show immediately. Otherwise
 * CP's Custom Trigger — firing from the same Chat-button click — opens the
 * journey and watchFrame reveals it on the state flip.
 *
 * @param {object} component  Alpine component
 */
Chat.open = function (component) {

  var _this = this;

  _this.suppressed = false;

  var frame = document.getElementById('chili-chat');

  if (frame && frame.classList.contains('expanded')) {

    document.body.classList.add('cb-chat-open');

    component.chatOpen = true;

  }

};

/**
 * Hide the widget without touching CP's internal state (CP has no close
 * API), and suppress the state-sync so it doesn't immediately re-show.
 * Used by the Chat toggle and the other buttons' one-panel-at-a-time
 * behavior.
 *
 * @param {object} component  Alpine component
 */
Chat.hide = function (component) {

  var _this = this;

  _this.suppressed = true;

  document.body.classList.remove('cb-chat-open');

  component.chatOpen = false;

};

export default Chat;
